import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiUsers,
  FiFileText,
  FiTrendingUp,
  FiAward,
  FiShield,
  FiHash,
  FiMessageSquare,
  FiStar,
} from "react-icons/fi";
import { useCommunitySpecificSidebar } from "../../../hooks/useCommunitySpecificSidebar";
import "./CommunitySpecificSidebar.css";

/**
 * CommunitySpecificSidebar - Sidebar widgets for community discussion page
 * Widgets: Community Info, Rules, Top Contributors, Trending Topics, Moderators
 */
const CommunitySpecificSidebar = ({ communityId }) => {
  const navigate = useNavigate();

  const {
    communityInfo,
    rules,
    topContributors,
    trendingTopics,
    moderators,
    loading,
  } = useCommunitySpecificSidebar(communityId);

  // Format count helper
  const formatCount = (count) => {
    if (!count) return "0";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Loading skeleton
  const WidgetSkeleton = () => (
    <div className="css-widget-skeleton">
      <div className="skeleton-line short"></div>
      <div className="skeleton-line"></div>
      <div className="skeleton-line medium"></div>
    </div>
  );

  return (
    <div className="css-widgets">
      {/* Community Info Card */}
      <motion.div
        className="css-widget css-widget-info"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {loading ? (
          <WidgetSkeleton />
        ) : communityInfo ? (
          <div className="css-info-content">
            <div className="css-info-header">
              <div className="css-info-avatar">
                {communityInfo.signedPhotoURL || communityInfo.photoURL ? (
                  <img
                    src={communityInfo.signedPhotoURL || communityInfo.photoURL}
                    alt={communityInfo.name}
                  />
                ) : (
                  <span>{communityInfo.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="css-info-details">
                <h3 className="css-info-name">{communityInfo.name}</h3>
                {communityInfo.field && (
                  <span className="css-info-field">{communityInfo.field}</span>
                )}
              </div>
            </div>
            {communityInfo.description && (
              <p className="css-info-description">{communityInfo.description}</p>
            )}
            <div className="css-info-stats">
              <div className="css-stat">
                <FiUsers size={14} />
                <span>{formatCount(communityInfo.memberCount)}</span>
                <span className="css-stat-label">Members</span>
              </div>
              <div className="css-stat">
                <FiMessageSquare size={14} />
                <span>{formatCount(communityInfo.threadCount)}</span>
                <span className="css-stat-label">Threads</span>
              </div>
            </div>
          </div>
        ) : null}
      </motion.div>

      {/* Community Rules */}
      <motion.div
        className="css-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="css-widget-header">
          <FiFileText size={16} className="css-widget-icon rules" />
          <h3>Community Rules</h3>
        </div>
        <div className="css-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : rules.length > 0 ? (
            <div className="css-rules-list">
              {rules.map((rule, index) => (
                <div key={rule.number || index} className="css-rule-item">
                  <span className="css-rule-number">{rule.number || index + 1}</span>
                  <div className="css-rule-content">
                    <strong className="css-rule-title">{rule.title}</strong>
                    {rule.description && (
                      <p className="css-rule-desc">{rule.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="css-widget-empty">
              <FiFileText size={20} />
              <span>No rules defined</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Top Contributors */}
      <motion.div
        className="css-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="css-widget-header">
          <FiAward size={16} className="css-widget-icon contributors" />
          <h3>Top Contributors</h3>
        </div>
        <div className="css-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : topContributors.length > 0 ? (
            <div className="css-contributors-list">
              {topContributors.map((contributor, index) => (
                <div
                  key={contributor.user?._id || index}
                  className="css-contributor-item"
                  onClick={() => navigate(`/profile/${contributor.user?._id}`)}
                >
                  <span className="css-contributor-rank">#{index + 1}</span>
                  <div className="css-contributor-avatar">
                    {contributor.user?.profilePhotoUrl ? (
                      <img
                        src={contributor.user.profilePhotoUrl}
                        alt={contributor.user.name}
                      />
                    ) : (
                      <span>{contributor.user?.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="css-contributor-info">
                    <span className="css-contributor-name">{contributor.user?.name}</span>
                    <span className="css-contributor-score">
                      <FiStar size={10} /> {formatCount(contributor.contributionScore)} pts
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="css-widget-empty">
              <FiAward size={20} />
              <span>No contributors yet</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Trending Topics */}
      <motion.div
        className="css-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="css-widget-header">
          <FiTrendingUp size={16} className="css-widget-icon trending" />
          <h3>Trending Topics</h3>
        </div>
        <div className="css-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : trendingTopics.length > 0 ? (
            <div className="css-topics-list">
              {trendingTopics.map((topic, index) => (
                <Link
                  key={topic.tag || index}
                  to={`/communities/${communityId}?tag=${encodeURIComponent(topic.tag)}`}
                  className="css-topic-tag"
                >
                  <FiHash size={12} />
                  <span>{topic.tag}</span>
                  {topic.count && (
                    <span className="css-topic-count">{formatCount(topic.count)}</span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="css-widget-empty">
              <FiTrendingUp size={20} />
              <span>No trending topics</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Moderators */}
      <motion.div
        className="css-widget"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="css-widget-header">
          <FiShield size={16} className="css-widget-icon moderators" />
          <h3>Moderators</h3>
        </div>
        <div className="css-widget-content">
          {loading ? (
            <WidgetSkeleton />
          ) : moderators ? (
            <div className="css-moderators-list">
              {/* Creator */}
              {moderators.creator && (
                <div
                  className="css-moderator-item css-moderator-creator"
                  onClick={() => navigate(`/profile/${moderators.creator._id}`)}
                >
                  <div className="css-moderator-avatar">
                    {moderators.creator.profilePhotoUrl ? (
                      <img
                        src={moderators.creator.profilePhotoUrl}
                        alt={moderators.creator.name}
                      />
                    ) : (
                      <span>{moderators.creator.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="css-moderator-info">
                    <span className="css-moderator-name">{moderators.creator.name}</span>
                    <span className="css-moderator-role creator">Creator</span>
                  </div>
                </div>
              )}
              {/* Moderators */}
              {moderators.moderators?.map((mod) => (
                <div
                  key={mod._id}
                  className="css-moderator-item"
                  onClick={() => navigate(`/profile/${mod._id}`)}
                >
                  <div className="css-moderator-avatar">
                    {mod.profilePhotoUrl ? (
                      <img src={mod.profilePhotoUrl} alt={mod.name} />
                    ) : (
                      <span>{mod.name?.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <div className="css-moderator-info">
                    <span className="css-moderator-name">{mod.name}</span>
                    <span className="css-moderator-role">Moderator</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="css-widget-empty">
              <FiShield size={20} />
              <span>No moderators</span>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CommunitySpecificSidebar;
